import React, { useState, useEffect } from "react";
import api from '../api';
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../css/EnterpriseDetails.css";
import { formatDateToUserTimeZone } from "../utils/formatDate";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import crossIcon from "../assets/icons/crossIcon.png";
import editIcon from "../assets/Images/Edit.png";
import viewIcon from "../assets/Images/View.png";
import changeStatusIcon from "../assets/Images/ChangeStatus.png";
import addIcon from "../assets/Images/AddIcon.png";

import downloadIcon from "../assets/Images/Download.png";
import filterIcon from "../assets/Images/Filter.png";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useDispatch } from "react-redux";
import { setRefreshSidebar } from "../redux/sidebarSlice";

import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../utils/toastMessages";
import modelsIcon from "../assets/icons/modulesvg.png";

const BASE_URL = `${process.env.REACT_APP_API_URL}/modules`;

const ModuleManagement = () => {
  const [modules, setModules] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    module_name: "",
    module_description: "",
    module_icon: null,
  });
  const [viewData, setViewData] = useState(null);
  const [editingModuleId, setEditingModuleId] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");
  const [dayFilter, setDayFilter] = useState("");
  const MySwal = withReactContent(Swal);
  const dispatch = useDispatch();

  const [moduleCounts, setModuleCounts] = useState({
    totalModules: 0,
    activeModules: 0,
    inactiveModules: 0,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchModuleCounts = async () => {
    const token =
      localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
    const res = await api.get(`${BASE_URL}/status-summary`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    // map backend snake_case keys to camelCase
    setModuleCounts({
      totalModules: res.data.total_modules,
      activeModules: res.data.active_modules,
      inactiveModules: res.data.inactive_modules,
    });
  };

  useEffect(() => {
    fetchModuleCounts();
  }, []);

  const token =
    localStorage.getItem("access_token") || sessionStorage.getItem("access_token");

  const fetchModules = async () => {
    if (!token) return showErrorToast(TOAST_MESSAGES.UNAUTHORIZED);

    try {
      const response = await api.get(BASE_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setModules(response.data);
    } catch (error) {
      console.error("Failed to fetch modules", error);
      showErrorToast(TOAST_MESSAGES.MODULE.FETCH_ERROR);
    }
  };

  useEffect(() => {
    fetchModules();
  }, []);

  const handleReset = () => {
    setSortOrder("asc");
    setDayFilter("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleApply = () => {
    console.log("Sorting by:", sortOrder);
    console.log("Filtering by day:", dayFilter);
  };

  const filteredModules = modules
    .filter((mod) => {
      const lower = searchTerm.toLowerCase();
      const name = mod.module_name ? mod.module_name.toLowerCase() : "";
      const desc = mod.module_description
        ? mod.module_description.toLowerCase()
        : "";
      const matchesSearch = name.includes(lower) || desc.includes(lower);

      if (!matchesSearch) return false;

      if (dayFilter === "Today" || dayFilter === "Yesterday") {
        const now = new Date();
        const modDate = new Date(mod.updated_timestamp);
        const today = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate()
        );
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);

        if (dayFilter === "Today") {
          return modDate >= today;
        } else {
          return modDate >= yesterday && modDate < today;
        }
      }

      return true;
    })
    .sort((a, b) => {
      const dateA = new Date(a.updated_timestamp);
      const dateB = new Date(b.updated_timestamp);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

  const totalEntries = filteredModules.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentData = filteredModules.slice(indexOfFirst, indexOfLast);
  const showingFrom = totalEntries === 0 ? 0 : indexOfFirst + 1;
  const showingTo = Math.min(indexOfLast, totalEntries);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageClick = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
  };

  const handleChangeStatus = async (mod) => {
    const currentStatus = mod.module_status; // Assuming "1" = Active, "0" = Inactive
    const newStatusText = currentStatus === "1" ? "Inactive" : "Active";

    try {
      const res = await fetch(`${BASE_URL}/change-status/${mod.module_id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Failed to change status");

      await fetchModules();
      dispatch(setRefreshSidebar(true));
      fetchModuleCounts();

      showSuccessToast(
        `${TOAST_MESSAGES.MODULE.STATUS_CHANGE_SUCCESS} to ${newStatusText}`
      );
    } catch (err) {
      console.error(err);
      showErrorToast(TOAST_MESSAGES.MODULE.STATUS_CHANGE_ERROR);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this module?")) return;
    try {
      const res = await fetch(`${BASE_URL}/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Delete failed");
      await fetchModules();
      if (currentData.length === 1 && currentPage > 1)
        setCurrentPage(currentPage - 1);
    } catch (err) {
      console.error(err);
    }
  };

  const handleEdit = (mod) => {
    setFormData({
      module_name: mod.module_name,
      module_description: mod.module_description || "",
      module_icon: null,
      existingIconName: mod.module_icon ? mod.module_icon.split("/").pop() : "",
    });

    setEditingModuleId(mod.module_id);
    setShowForm(true);
  };

  const handleView = (mod) => {
    setViewData(mod);
    setFormData({
      module_name: mod.module_name,
      module_description: mod.module_description || "",
      module_icon: null,
      existingIconName: mod.module_icon ? mod.module_icon.split("/").pop() : "",
    });
    setShowForm(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true); // Start loading

    try {
      const formPayload = new FormData();
      formPayload.append("module_name", formData.module_name);
      formPayload.append("module_description", formData.module_description);
      if (formData.module_icon) {
        formPayload.append("icon", formData.module_icon);
      }

      let res;
      if (editingModuleId) {
        res = await fetch(`${BASE_URL}/${editingModuleId}`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
          body: formPayload,
        });
      } else {
        res = await fetch(BASE_URL, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formPayload,
        });
      }

      // if (res.status === 401) {
      //   MySwal.fire({
      //     icon: "warning",
      //     title: "Session expired or unauthorized. Please log in again.",
      //   });
      //   return;
      // }
      
      if (!res.ok)
        throw new Error(editingModuleId ? "Update failed" : "Create failed");

      setFormData({ module_name: "", description: "", icon: null });
      setShowForm(false);
      setEditingModuleId(null);
      setViewData(null);
      await fetchModules();
      dispatch(setRefreshSidebar(true));

      showSuccessToast(
        editingModuleId
          ? TOAST_MESSAGES.MODULE.UPDATE_SUCCESS
          : TOAST_MESSAGES.MODULE.CREATE_SUCCESS
      );
    } catch (err) {
      console.error(err);
      showErrorToast(
        editingModuleId
          ? TOAST_MESSAGES.MODULE.UPDATE_ERROR
          : TOAST_MESSAGES.MODULE.CREATE_ERROR
      );
    } finally {
      setIsSubmitting(false); // Stop loading
    }
  };

  const closeModal = () => {
    setShowForm(false);
    setFormData({ module_name: "", description: "", icon: null });
    setEditingModuleId(null);
    setViewData(null);
  };

  const exportExcel = () => {
    const wsData = [
      ["Module", "Description", "Status"],
      ...filteredModules.map((m) => [
        m.module_name,
        m.module_description || "",
        m.module_status === "1" ? "Active" : "Inactive",
        formatDateToUserTimeZone(m.created_timestamp),
        formatDateToUserTimeZone(m.updated_timestamp),
        m.created_by || "",
        m.updated_by || "",
      ]),
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "Modules");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      "module_management.xlsx"
    );
  };

  return (
    <div className="mx-2 pt-1 h-100">
      {/* Module - Total */}

      <div className="d-flex flex-direction-column flex-wrap gap-2 mt-0 mb-2 justify-content-between">
        <div className="dashboard-box p-3 rounded shadow-sm">
          <div className="d-flex align-items-center mb-2">
            <img
              src={modelsIcon}
              alt="Total Modules"
              style={{
                width: "22px",
                height: "22px",
              }}
              className="icon-image1"
            />
            <h6 className="ms-2 mb-0">Total Modules</h6>
          </div>
          <h2 className="fw-bold">{moduleCounts.totalModules}</h2>
        </div>

        {/* Module - Active */}
        <div className="dashboard-box p-3 rounded shadow-sm">
          <div className="d-flex align-items-center mb-2">
            <img
              src={modelsIcon}
              alt="Active Modules"
              style={{
                width: "22px",
                height: "22px",
              }}
              className="icon-image1"
            />
            <h6 className="ms-2 mb-0">Active Modules</h6>
          </div>
          <h2 className="fw-bold">{moduleCounts.activeModules}</h2>
        </div>

        {/* Module - Inactive */}
        <div className="dashboard-box p-3 rounded shadow-sm">
          <div className="d-flex align-items-center mb-2">
            <img
              src={modelsIcon}
              alt="Inactive Modules"
              style={{
                width: "22px",
                height: "22px",
              }}
              className="icon-image1"
            />
            <h6 className="ms-2 mb-0">Inactive Modules</h6>
          </div>
          <h2 className="fw-bold">{moduleCounts.inactiveModules}</h2>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2 mt-1 ">
        <span className="fw-bold refresh-prompts" style={{ fontSize: "20px" }}>
          Module Management Details
        </span>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center">
          <select
            className="form-select w-auto"
            value={entriesPerPage}
            onChange={(e) => {
              setEntriesPerPage(+e.target.value);
              setCurrentPage(1);
            }}
          >
            {[50, 100, 500].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <label className="ms-2 mt-2">Entries per page</label>
        </div>

        <div className="d-flex align-items-center">
          <div className="custom-search-wrapper me-3">
            <div className="input-group custom-search-group">
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search Modules"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          {/* <button
            className="btn btn-outline-secondary me-2 action-button"
            onClick={exportExcel}
            title="Export to Excel"
          >
            <img src={downloadIcon} alt="Download" />
          </button> */}

          <div className="">
            <button
              className="btn icon-button filter-dropdown-toggle action-button"
              data-bs-toggle="dropdown"
              title="Filter"
            >
              <img src={filterIcon} className="icon-image" alt="Filter" />
            </button>
            <div
              className="dropdown-menu profile-menu-dropdown p-2"
              style={{ minWidth: "200px" }}
            >
              <h6 className="fw-bold refresh-prompts">Time</h6>
              {["asc", "desc"].map((order) => (
                <div className="form-check" key={order}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="sortOrder"
                    id={order}
                    value={order}
                    checked={sortOrder === order}
                    onChange={() => setSortOrder(order)}
                  />
                  <label className="form-check-label" htmlFor={order}>
                    {order === "asc" ? "Ascending" : "Descending"}
                  </label>
                </div>
              ))}

              <hr className="my-2" />
              <h6 className="fw-bold refresh-prompts">By Day</h6>
              {["Today", "Yesterday", ""].map((day) => (
                <div className="form-check" key={day || "all"}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="dayFilter"
                    id={day || "all"}
                    value={day}
                    checked={dayFilter === day}
                    onChange={() => setDayFilter(day)}
                  />
                  <label className="form-check-label" htmlFor={day || "all"}>
                    {day || "All"}
                  </label>
                </div>
              ))}
              <div className="d-flex justify-content-between mt-3">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleReset}
                >
                  Reset
                </button>
                <button
                  className="btn bg-dark text-white btn-sm"
                  onClick={handleApply}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>

          <button
            className="btn btn-outline-secondary action-button text-dark"
            onClick={() => setShowForm(true)}
            title="Add Module"
          >
            <img src={addIcon} className="icon-image" alt="Add" />
          </button>
        </div>
      </div>

      {totalEntries === 0 ? (
        <div className="text-center my-5">
          <p className="text-muted fs-5">No entries found</p>
        </div>
      ) : (
        <div className="not-disable">
          <div className="table-responsive">
            <table className="table module-table table-sm w-100">
              <thead>
                <tr>
                  <th>Module</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Last Updated</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((m) => (
                  <tr key={m.module_id}>
                    <td className="d-flex align-items-center gap-2">
                      {m.module_icon && (
                        <img
                          src={`${process.env.REACT_APP_SERVER_URL}/${m.module_icon}`}
                          alt="Icon"
                          style={{
                            width: "14px",
                            height: "14px",
                            objectFit: "contain",
                          }}
                          className="icon-image"
                        />
                      )}
                      {m.module_name}
                    </td>
                    <td title={m.module_description}>
                      {m.module_description || "—"}
                    </td>
                    <td>{m.module_status === "1" ? "Active" : "Inactive"}</td>
                    <td>{formatDateToUserTimeZone(m.updated_timestamp)}</td>

                    <td>
                      <button
                        className="icon-button"
                        onClick={() => handleView(m)}
                        title="View"
                      >
                        <img
                          src={viewIcon}
                          className="icon-image"
                          alt="View"
                          style={{ height: "16px", width: "18px" }}
                        />
                      </button>

                      <button
                        className="icon-button"
                        onClick={() => handleEdit(m)}
                        title="Edit"
                      >
                        <img src={editIcon} className="icon-image" alt="Edit" />
                      </button>

                      <button
                        className="icon-button"
                        onClick={() => handleChangeStatus(m)}
                        title="Change Status"
                      >
                        <img
                          src={changeStatusIcon}
                          className="icon-image"
                          alt="Change Status"
                        />
                      </button>

                      {/* <button
                        className="icon-button"
                        onClick={() => handleDelete(m.module_id)}
                        title="Delete"
                      >
                        <i className="bi bi-trash text-danger"></i>
                      </button> */}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* <div className="d-flex justify-content-between align-items-center mt-2 table-footer"> */}
          <div className="table-footer bgcolor fixed-bottom-footer d-flex justify-content-between align-items-center">
            <div className="refresh-prompts">
              Showing {showingFrom} to {showingTo} of {totalEntries} entries
            </div>
            <nav>
              <ul className="pagination mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(1)}
                  >
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(currentPage - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>
                {pageNumbers.map((num) => (
                  <li
                    key={num}
                    className={`page-item ${
                      num === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageClick(num)}
                    >
                      {num}
                    </button>
                  </li>
                ))}
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(currentPage + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(totalPages)}
                  >
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}

      {(showForm || viewData) && (
        <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content w-50 mx-auto">
              <form onSubmit={handleFormSubmit}>
                <div className="modal-header  justify-content-center position-relative">
                  <h5 className="modal-title m-0">
                    {viewData
                      ? "View Module"
                      : editingModuleId
                      ? "Edit Module"
                      : "Add Module"}
                  </h5>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={closeModal}
                    aria-label="Close"
                  >
                    <img src={crossIcon} className="icon-image" alt="Close" />
                  </button>
                </div>
                <div className="modal-body">
                  <div className="mb-3 d-flex align-items-center">
                    <div className="me-2">
                      <label className="form-label ms-4">Icon</label>
                      <div className="d-flex align-items-center">
                        {formData.existingIconName && (
                          <img
                            src={`${process.env.REACT_APP_SERVER_URL}/${formData.existingIconName}`}
                            alt="Icon Preview"
                            style={{
                              width: "32px",
                              height: "32px",
                              borderRadius: "50%",
                              objectFit: "cover",
                              border: "1px solid #ccc",
                              marginRight: "8px",
                            }}
                            className="icon-image"
                          />
                        )}
                        {
                          <input
                            type="file"
                            className="file-input-reverse"
                            accept="image/*"
                            disabled={!!viewData}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                module_icon: e.target.files[0],
                              })
                            }
                          />
                        }
                      </div>
                    </div>
                    <div className="flex-grow-1">
                      <label className="form-label">Module Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.module_name}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            module_name: e.target.value,
                          })
                        }
                        required
                        disabled={!!viewData}
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="input-textarea1 p-1"
                      value={formData.module_description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          module_description: e.target.value,
                        })
                      }
                      disabled={!!viewData}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  {viewData ? (
                    <button
                      type="button"
                      className="cancle-btn"
                      onClick={closeModal}
                    >
                      Close
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="px-5 rounded custom-btn py-2 fw-bold"
                      disabled={isSubmitting} // prevent double submit
                    >
                      {isSubmitting
                        ? editingModuleId
                          ? "Updating..."
                          : "Adding..."
                        : editingModuleId
                        ? "Update Module"
                        : "Add Module"}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModuleManagement;
